import Link from 'next/link';
import { fraunces } from '../Landing/fonts';
import { Stamp } from '../Landing/ui';
import FutureImage from '../FutureImage';
import imageCrop from 'helpers/imageCrop';
import { bboxCenterLine, flagSrc } from './FieldNotesPlate';
import PhotoCredit from './PhotoCredit';

// Country names run from "Chad" to "Bosnia and Herzegovina" — step the
// display size down by length so the name never wraps awkwardly.
function titleSizeClasses(title) {
  const length = (title || '').length;
  if (length > 18)
    return 'tw-text-[2.4rem] sm:tw-text-[3.6rem] lg:tw-text-[4.5rem]';
  if (length > 11)
    return 'tw-text-[2.8rem] sm:tw-text-[4.4rem] lg:tw-text-[5.5rem]';
  return 'tw-text-[3.4rem] sm:tw-text-[5.5rem] lg:tw-text-[7rem]';
}

// The country guide's cinematic cover: full-bleed photo, the guidebook
// title block pinned lower-left, warm paper rolling in under the organic
// curved edge — same hero language as the post masthead, scaled up to a
// chapter opener. Carries the page's ONE passport stamp, and keeps the
// legally required photo credit + curator edit pencil on the photo.
export default function GuideHero({
  image,
  blur,
  alt,
  title,
  kicker,
  breadcrumbs,
  countryCode,
  bbox,
  capitalCity,
  locationDetails,
  editButton,
}) {
  const flag = flagSrc(countryCode);
  const coords = bboxCenterLine(bbox);
  const isUnsplash = Boolean(locationDetails && locationDetails.unsplashUser);
  // Unsplash wants its CDN hot-linked (and already carries sizing params);
  // everything else goes through our proxy exactly once via imageCrop.
  const src = image
    ? isUnsplash
      ? image.replace(/&fm=jpg/, '&fm=webp')
      : imageCrop(image, 2000)
    : null;

  const metaParts = [];
  if (coords) metaParts.push(coords);
  if (capitalCity) metaParts.push(`CAPITAL: ${capitalCity.toUpperCase()}`);

  return (
    <header className="tw-relative">
      <div className="tf-hero-guide tw-relative tw-overflow-hidden">
        {src ? (
          <>
            <FutureImage
              src={src}
              alt={alt || title}
              fill
              priority
              fetchPriority="high"
              unoptimized
              sizes="100vw"
              className="tw-object-cover"
              placeholder={blur ? 'blur' : undefined}
              blurDataURL={blur}
            />
            {/* Top scrim: the transparent reading nav sits on the photo */}
            <div
              aria-hidden="true"
              className="tw-absolute tw-inset-x-0 tw-top-0 tw-h-32 tw-bg-gradient-to-b tw-from-[rgba(12,20,17,0.5)] tw-to-transparent"
            />
            {/* Bottom scrim: title block legibility */}
            <div
              aria-hidden="true"
              className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-[75%] tw-bg-gradient-to-t tw-from-[rgba(10,18,15,0.8)] tw-via-[rgba(10,18,15,0.32)] tw-to-transparent"
            />
          </>
        ) : (
          // Destinations without a photo: quiet paper with contour lines.
          <div className="tw-absolute tw-inset-0 tw-bg-[#F6F3E9] dark:tw-bg-[#15231E]">
            <svg
              aria-hidden="true"
              className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-text-[#1D2D28] tw-opacity-[0.05] dark:tw-text-[#EBE6D9]"
              preserveAspectRatio="xMidYMid slice"
              viewBox="0 0 800 400"
            >
              <g fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M-40 60c120-45 240 35 360-5s215-60 360-15 160 55 160 55" />
                <path d="M-40 105c120-45 240 35 360-5s215-60 360-15 160 55 160 55" />
                <path d="M-40 330c135-60 255 45 375 0s200-75 345-20 160 60 160 60" />
              </g>
            </svg>
          </div>
        )}

        {/* Title block pinned lower-left, mirroring the field-notes plate */}
        <div
          className={`tw-absolute tw-inset-x-0 tw-bottom-0 tw-px-[6vw] tw-pb-14 sm:tw-pb-16 ${
            src ? '[text-shadow:0_1px_14px_rgba(0,0,0,0.45)]' : ''
          }`}
        >
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav
              aria-label="Breadcrumb"
              className={`tw-mb-3.5 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.22em] ${
                src ? 'tw-text-[rgba(255,255,255,0.75)]' : 'tw-text-[#8A958F]'
              }`}
            >
              {breadcrumbs.map((crumb, i) => (
                <span key={crumb.href}>
                  {i > 0 && <span aria-hidden="true"> → </span>}
                  <Link
                    href={crumb.href}
                    prefetch={false}
                    className={`tw-no-underline ${
                      src
                        ? 'tw-text-[rgba(255,255,255,0.75)] hover:tw-text-white'
                        : 'tw-text-[#8A958F] hover:tw-text-primary-700'
                    }`}
                  >
                    {crumb.name.toUpperCase()}
                  </Link>
                </span>
              ))}
            </nav>
          )}

          {(flag || kicker) && (
            <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">
              {flag && (
                <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[rgba(255,255,255,0.88)] tw-px-2 tw-py-1">
                  <img
                    src={flag}
                    alt=""
                    aria-hidden="true"
                    width={16}
                    height={16}
                    loading="eager"
                    className="tw-h-4 tw-w-4"
                  />
                </span>
              )}
              {kicker && (
                <span
                  className={`tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] ${
                    src ? 'tw-text-primary-200' : 'tw-text-primary-700'
                  }`}
                  // The kicker contains the current year — a cached page from
                  // last year would otherwise warn at the year boundary.
                  suppressHydrationWarning
                >
                  {kicker}
                </span>
              )}
            </div>
          )}

          <h1
            className={`${fraunces.className} ${titleSizeClasses(
              title,
            )} tw-m-0 tw-font-semibold tw-leading-[0.98] tw-tracking-tight ${
              src ? 'tw-text-white' : 'tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]'
            }`}
          >
            {title}
            <em
              className={`tw-mt-2 tw-block tw-text-[0.34em] tw-font-medium tw-italic tw-leading-snug ${
                src ? 'tw-text-primary-200' : 'tw-text-primary-700'
              }`}
            >
              travel guide
            </em>
          </h1>

          {metaParts.length > 0 && (
            <p
              className={`tw-m-0 tw-mt-5 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.22em] ${
                src ? 'tw-text-[rgba(255,255,255,0.7)]' : 'tw-text-[#8A958F]'
              }`}
            >
              {metaParts.join(' · ')}
            </p>
          )}
        </div>

        {/* Photo credit + curator pencil — legally required, stays on the
            photo it credits. Top-right, clear of the stamp on the seam. */}
        <div
          className={`tw-absolute tw-right-[6vw] tw-top-[4.5rem] tw-z-[4] tw-inline-flex tw-items-center tw-gap-1 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.18em] ${
            src ? 'tw-text-[rgba(255,255,255,0.6)]' : 'tw-text-[#8A958F]'
          }`}
        >
          <PhotoCredit locationDetails={locationDetails} />
          {editButton}
        </div>

        {/* Organic paper edge — currentColor so the theme swap is pure CSS */}
        <svg
          aria-hidden="true"
          preserveAspectRatio="none"
          viewBox="0 0 1440 64"
          className="tw-absolute tw-inset-x-0 -tw-bottom-px tw-block tw-h-7 tw-w-full tw-text-[#FAF8F1] dark:tw-text-[#0E1A16] sm:tw-h-11"
        >
          <path
            fill="currentColor"
            d="M0 38 C 240 64, 480 6, 760 18 C 1020 29, 1240 10, 1440 34 L1440 64 L0 64 Z"
          />
        </svg>
      </div>

      {/* The page's one passport stamp, straddling the photo/paper seam */}
      <Stamp
        className="tw-absolute tw-right-[6.5%] tw-hidden tw-h-[5.5rem] tw-w-[5.5rem] -tw-translate-y-[55%] tw-rotate-12 tw-text-amber-500/90 sm:tw-block"
        text="TRAVELFEED · FIELD NOTES · "
      />
    </header>
  );
}
